import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  first_name: z.string().trim().min(1, "First name is required"),
  last_name: z.string().trim().min(1, "Last name is required"),
  phone: z.string().trim().min(7, "Enter a valid phone number"),
  email: z.string().trim().email("Enter a valid email address"),
  practice_name: z.string().trim().min(1, "Practice name is required"),
  implant_system: z.string().trim().min(1, "Please tell us your implant system"),
  outside_lab: z.enum(["Yes", "No"], {
    errorMap: () => ({ message: "Please select an option" }),
  }),
});

const FieldError = ({ msg }) =>
  msg ? <p className="mt-1 text-xs text-destructive">{msg}</p> : null;

export const LeadForm = ({
  source,
  tags,
  submitLabel = "Apply for Founding Membership",
  privacyText = "We\u2019ll only use your info to follow up about FABG. No obligation.",
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      practice_name: "",
      implant_system: "",
      outside_lab: undefined,
    },
  });

  const outsideLab = watch("outside_lab");

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...values,
        ...(source ? { source } : {}),
        ...(tags && tags.length ? { tags } : {}),
      });
      toast.success("Submission received! Redirecting…");
      navigate("/thank-you");
    } catch (e) {
      console.error("Lead submission failed", e);
      toast.error("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const inputClasses =
    "h-11 rounded-xl bg-white focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      data-testid="founding-membership-form"
      className="space-y-4"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name" className="mb-1.5 block text-sm font-medium">
            First name
          </Label>
          <Input
            id="first_name"
            data-testid="founding-membership-first-name-input"
            className={inputClasses}
            placeholder="Jane"
            {...register("first_name")}
          />
          <FieldError msg={errors.first_name?.message} />
        </div>
        <div>
          <Label htmlFor="last_name" className="mb-1.5 block text-sm font-medium">
            Last name
          </Label>
          <Input
            id="last_name"
            data-testid="founding-membership-last-name-input"
            className={inputClasses}
            placeholder="Doe"
            {...register("last_name")}
          />
          <FieldError msg={errors.last_name?.message} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email" className="mb-1.5 block text-sm font-medium">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="founding-membership-email-input"
            className={inputClasses}
            placeholder="you@practice.com"
            {...register("email")}
          />
          <FieldError msg={errors.email?.message} />
        </div>
        <div>
          <Label htmlFor="phone" className="mb-1.5 block text-sm font-medium">
            Phone
          </Label>
          <Input
            id="phone"
            type="tel"
            data-testid="founding-membership-phone-input"
            className={inputClasses}
            placeholder="(555) 123-4567"
            {...register("phone")}
          />
          <FieldError msg={errors.phone?.message} />
        </div>
      </div>

      <div>
        <Label htmlFor="practice_name" className="mb-1.5 block text-sm font-medium">
          Practice name
        </Label>
        <Input
          id="practice_name"
          data-testid="founding-membership-practice-name-input"
          className={inputClasses}
          placeholder="Your practice name"
          {...register("practice_name")}
        />
        <FieldError msg={errors.practice_name?.message} />
      </div>

      <div>
        <Label htmlFor="implant_system" className="mb-1.5 block text-sm font-medium">
          What implant system do you use?
        </Label>
        <Input
          id="implant_system"
          data-testid="founding-membership-implant-system-input"
          className={inputClasses}
          placeholder="e.g. Nobel, Straumann, Neodent, BioHorizons…"
          {...register("implant_system")}
        />
        <FieldError msg={errors.implant_system?.message} />
      </div>

      <div>
        <Label htmlFor="outside_lab" className="mb-1.5 block text-sm font-medium">
          Do you use an outside lab?
        </Label>
        <Select
          value={outsideLab || ""}
          onValueChange={(v) =>
            setValue("outside_lab", v, { shouldValidate: true })
          }
        >
          <SelectTrigger
            id="outside_lab"
            data-testid="founding-membership-outside-lab-select"
            className="h-11 rounded-xl bg-white focus:ring-2 focus:ring-ring"
          >
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
        <FieldError msg={errors.outside_lab?.message} />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="founding-membership-submit-button"
        className="w-full h-12 rounded-xl text-base font-semibold shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98]"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Submitting…
          </>
        ) : (
          submitLabel
        )}
      </Button>

      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5" />
        {privacyText}
      </p>
    </form>
  );
};
